import React, {
  useMemo,
  useState,
} from "react";
import {
  ChevronLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useCorridor } from "../hooks/useCorridor";
import {
  useMe,
  usePayoutBanks,
} from "../hooks/useMe";
import { useCountries } from "../hooks/useCountries";
import { useSupportedRecipientCountries } from "../hooks/useSupportedRecipientCountries";
import { useSupportedChannels } from "../hooks/useSupportedChannels";
import { isChannelSupported } from "../data/payoutCoverage";
import { useTransferFlow } from "../context/TransferFlowContext";
import { safeApiErrorMessage } from "../utils/safeApiErrorMessage";

import { RecipientForm } from "./recipient/RecipientForm";

export function CreateRecipient() {
  const navigate = useNavigate();

  /*
   * Keep these hooks here rather than moving them
   * into RecipientForm.
   *
   * This preserves the existing page behaviour and
   * the existing CreateRecipient test suite.
   */
  const corridor = useCorridor();

  const { setRecipientId } =
    useTransferFlow();

  const {
    createRecipientAsync,
  } = useMe();

  const [nickname, setNickname] =
    useState("");

  const [
    firstName,
    setFirstName,
  ] = useState("");

  const [
    lastName,
    setLastName,
  ] = useState("");

  const [
    submitting,
    setSubmitting,
  ] = useState(false);

  const [
    submitError,
    setSubmitError,
  ] = useState(null);

  const { countries } =
    useCountries();

  /*
   * Same BluPay payout-market restriction
   * as before.
   */
  const { countryOptions } =
    useSupportedRecipientCountries(
      corridor.countries,
      countries
    );

  const selectedCountry =
    useMemo(
      () =>
        countryOptions.find(
          (country) =>
            country.value ===
            corridor.countryCode
        ) || null,
      [
        countryOptions,
        corridor.countryCode,
      ]
    );

  const selectedCountryData =
    useMemo(
      () =>
        countries.find(
          (country) =>
            country.code?.toUpperCase() ===
            corridor.countryCode?.toUpperCase()
        ) || null,
      [
        countries,
        corridor.countryCode,
      ]
    );

  const recipientDialCode =
    selectedCountryData?.dialCode ||
    corridor.getDialCode() ||
    "";

  const isBankTransferWithBanks =
    isChannelSupported(
      corridor.countryCode,
      "bank_transfer"
    ) &&
    corridor.channelKey ===
      "bank_transfer";

  const {
    banks,
    isLoadingBanks,
  } = usePayoutBanks(
    isBankTransferWithBanks
      ? corridor.countryCode
      : null
  );

  const channelEntries =
    useSupportedChannels(
      corridor.countryCode,
      corridor.availableChannels
    );

  const fullName =
    `${firstName} ${lastName}`.trim();

  async function handleSubmit(intent) {
    setSubmitError(null);

    if (!corridor.countryCode) {
      setSubmitError(
        "Please select a country."
      );
      return;
    }

    if (!corridor.channelKey) {
      setSubmitError(
        "Please select how the recipient will receive funds."
      );
      return;
    }

    if (!fullName) {
      setSubmitError(
        "Please enter the recipient's name."
      );
      return;
    }

    if (!corridor.validate()) {
      setSubmitError(
        "Please fix the highlighted fields."
      );
      return;
    }

    const paymentMethod =
      corridor.buildPaymentMethodPayload();

    if (!paymentMethod) {
      setSubmitError(
        "Please fix the highlighted fields."
      );
      return;
    }

    const payload = {
      nickname:
        nickname || fullName,

      full_name:
        fullName,

      country_code:
        corridor.countryCode,

      currency:
        paymentMethod.currency,

      default_channel:
        paymentMethod.channel,

      default_provider:
        paymentMethod.provider,

      status:
        "active",

      payment_method:
        paymentMethod,
    };

    setSubmitting(true);

    try {
      const recipient =
        await createRecipientAsync(
          payload
        );

      if (intent === "send") {
        setRecipientId(
          recipient.id
        );

        navigate("/transfer");
      } else {
        navigate("/home");
      }
    } catch (error) {
      setSubmitError(
        safeApiErrorMessage(
          error,
          "Something went wrong creating the recipient."
        )
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-24 text-left">
      {/* Mobile header */}
      <div className="md:hidden bg-[#0A4730] rounded-b-[50px] px-6 pt-12 pb-8">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() =>
              navigate(-1)
            }
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:scale-95 transition"
          >
            <ChevronLeft
              className="text-white"
              size={20}
            />
          </button>

          <div>
            <p className="text-[12px] text-white opacity-80 mb-1">
              Recipients
            </p>

            <h1 className="text-2xl text-[#C0F6B7] font-serif font-semibold leading-tight">
              New Recipient
            </h1>
          </div>
        </div>
      </div>

      {/* Desktop header */}
      <div className="hidden md:flex items-center gap-3 max-w-xl mx-auto px-5 pt-10 pb-2">
        <button
          type="button"
          onClick={() =>
            navigate(-1)
          }
          className="w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center active:scale-95 transition text-[#0A4730]/60 hover:border-[#0A4730]/30"
        >
          <ChevronLeft
            size={18}
          />
        </button>

        <h1 className="text-2xl text-[#0A4730] font-serif font-semibold">
          New Recipient
        </h1>
      </div>

      <div className="max-w-xl mx-auto px-5 py-8">
        <RecipientForm
          nickname={nickname}
          setNickname={
            setNickname
          }
          firstName={
            firstName
          }
          setFirstName={
            setFirstName
          }
          lastName={
            lastName
          }
          setLastName={
            setLastName
          }
          corridor={
            corridor
          }
          countryOptions={
            countryOptions
          }
          selectedCountry={
            selectedCountry
          }
          channelEntries={
            channelEntries
          }
          recipientDialCode={
            recipientDialCode
          }
          banks={banks}
          isLoadingBanks={
            isLoadingBanks
          }
          submitting={
            submitting
          }
          submitError={
            submitError
          }
          onSave={() =>
            handleSubmit("save")
          }
          onSend={() =>
            handleSubmit("send")
          }
        />
      </div>
    </div>
  );
}